import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import BusinessUnitReportCard from "../../components/businessUnitReportCard/BusinessUnitReportCard";
import MonthGraph from "../../components/graphs/monthGraph/MonthGraph";
import DaysGraph from "../../components/graphs/daysGraph/DaysGraph";
import DashboardLayout from "../../components/layout/DashboardLayout";
import BannerContainer from "../../components/banners/BannerContainer";
import {
  fetchAllBusinessUnits24hReport,
  fetchAllBusinessUnitsCurrentDayReport,
  fetchAllBusinessUnitsCurrentMonthDaysReprot,
  fetchAllBusinessUnitsLast30DaysReprot,
  fetchAllBusinessUnitsLast6MonthsReports,
  fetchAllBusinessUnitsLast7DaysReprot,
  fetchAllBusinessUnitsYearReports,
} from "../../store/actions/user/userReport";
import styles from "./Dashboard.module.scss";
import { ThreeDots } from "svg-loaders-react";
import classNames from "classnames";
import TableGraph from "../../components/graphs/tableGraph/TableGraph";
import { isMobile } from "react-device-detect";
import MobileTableGraph from "../../components/graphs/mobileTableGraph/MobileTableGraph";
import BusinessUnitReportModal from "../../components/modals/businessUnitReportModal/BusinessUnitReportModal";
import moment from "moment";
import userReportService from "../../apis/services/user/userReportService";
import SecondaryButton from "../../components/secondaryButton/SecondaryButton";
import { XlsIcon, ChevronDown } from "../../assets";
import { getBusinessUnitsGraphReport } from "../../util/report";

const colors = ["#003f5c", "#58508d", "#bc5090", "#ff6361", "#ffa600"];

const reportOptions = [
  { value: "last12Months", label: "12 meseci", labelSm: "12m" },
  { value: "last6Months", label: "6 meseci", labelSm: "6m" },
  { value: "last7Days", label: "7 dana", labelSm: "7d" },
  { value: "last30Days", label: "30 dana", labelSm: "30d" },
  { value: "currentMonthDays", label: "Tekući mesec", labelSm: "1m" },
  { value: "last24Hours", label: "24h", labelSm: "24h" },
  { value: "customDateRange", label: "Proizvoljni period", labelSm: "Period" },
];

const viewOptions = ["Tabelarno", "Graficki"];

const Dashboard = () => {
  const user = useSelector((state) => state.auth.user);

  const dispatch = useDispatch();

  const [selectedBusinessUnit, setSelectedBusinessUnit] = useState(null);
  const allBusinessUnitsCurrentReport = useSelector(
    (state) => state.businessUnitReports.allBusinessUnitsCurrentReport
  );
  const allBusinessUnitsYearReports = useSelector(
    (state) => state.businessUnitReports.allBusinessUnitsYearReports
  );

  const allBusinessUnitsLast7DaysReport = useSelector(
    (state) => state.businessUnitReports.allBusinessUnitsLast7DaysReport
  );

  const allBusinessUnitsLast30DaysReport = useSelector(
    (state) => state.businessUnitReports.allBusinessUnitsLast30DaysReport
  );

  const allBusinessUnitsLast6MonthsReport = useSelector(
    (state) => state.businessUnitReports.allBusinessUnitsLast6MonthsReport
  );

  const allBusinessUnitsCurrentMonthDaysReport = useSelector(
    (state) => state.businessUnitReports.allBusinessUnitsCurrentMonthDaysReport
  );

  const allBusinessUnits24hReport = useSelector(
    (state) => state.businessUnitReports.allBusinessUnits24hReport
  );

  const [selectedBusinessUnits, setSelectedBusinessUnits] = useState([]);
  const [selectedRange, setSelectedRange] = useState(reportOptions[0]);
  const [rangePickerVisible, setRangePickerVisible] = useState(false);
  const [selectedViewOption, setSelectedViewOption] = useState("Graficki");
  const [customDateFrom, setCustomDateFrom] = useState(moment().startOf('week').format('YYYY-MM-DD'));
  const [customDateTo, setCustomDateTo] = useState(moment().endOf('day').format('YYYY-MM-DD'));
  const [customDateReport, setCustomDateReport] = useState({ data: [], attempt: false });
  const [datePickerPopupVisible, setDatePickerPopupVisible] = useState(false);
  const [customReportType, setCustomReportType] = useState("days");

  useEffect(() => {
    if (user?.companies) {
      dispatch(fetchAllBusinessUnitsYearReports());
      dispatch(fetchAllBusinessUnitsCurrentDayReport());
    }
  }, [user]);

  useEffect(() => {
    switch (true) {
      case selectedRange?.value === "last7Days" &&
        allBusinessUnitsLast7DaysReport.data.length === 0:
        dispatch(fetchAllBusinessUnitsLast7DaysReprot());
        break;
      case selectedRange?.value === "last12Months" &&
        allBusinessUnitsYearReports.data.length === 0:
        dispatch(fetchAllBusinessUnitsYearReports());
        break;
      case selectedRange?.value === "last6Months" &&
        allBusinessUnitsLast6MonthsReport.data.length === 0:
        dispatch(fetchAllBusinessUnitsLast6MonthsReports());
        break;
      case selectedRange?.value === "last30Days" &&
        allBusinessUnitsLast30DaysReport.data.length === 0:
        dispatch(fetchAllBusinessUnitsLast30DaysReprot());
        break;
      case selectedRange?.value === "currentMonthDays" &&
        allBusinessUnitsCurrentMonthDaysReport.data.length === 0:
        dispatch(fetchAllBusinessUnitsCurrentMonthDaysReprot());
        break;
      case selectedRange?.value === "last24Hours" &&
        allBusinessUnits24hReport.data.length === 0:
        dispatch(fetchAllBusinessUnits24hReport());
        break;
      case selectedRange?.value === "customDateRange" &&
        customDateFrom && customDateTo:
        fetchCustomDateRangeReport();
        break;
      default:
        return null;
    }
  }, [selectedRange, customDateFrom, customDateTo]);

  const fetchCustomDateRangeReport = async () => {
    if (!customDateFrom || !customDateTo) return;
    
    // Set attempt status to true
    setCustomDateReport({ ...customDateReport, attempt: true });
    
    try {
      // Format dates to ensure beginning of day for dateFrom and end of day for dateTo
      const dayBegin = moment(new Date(customDateFrom))
        .tz("Europe/Belgrade")
        .startOf("day")
        .format("x");
      
      const dayEnd = moment(new Date(customDateTo))
        .tz("Europe/Belgrade")
        .endOf("day")
        .format("x");
      
      // Use the selected report type instead of determining it automatically
      const type = customReportType; // "days" or "months"
      
      const { data } = await userReportService.fetchAllBusinessUnitsReport(
        dayBegin,
        dayEnd,
        type,
        false
      );
      
      // Process the data for graph rendering
      const { array, businessUnitsNames } = getBusinessUnitsGraphReport(data);
      
      // Update state with the fetched data
      setCustomDateReport({ 
        data: {
          array,
          businessUnitsNames,
        }, 
        attempt: false 
      });
      
      // If no businessUnits are selected, select the first one by default
      if (selectedBusinessUnits.length === 0 && businessUnitsNames && businessUnitsNames.length > 0) {
        setSelectedBusinessUnits([businessUnitsNames[0]]);
      }
      
    } catch (err) {
      // Handle errors
      setCustomDateReport({ ...customDateReport, attempt: false });
      console.error("Error fetching custom date range report:", err);
    }
  };

  const onExport = async () => {
    let dayBegin;
    let dayEnd;
    let type;
    switch (true) {
      case selectedRange?.value === "last7Days":
        dayBegin = moment()
          .tz("Europe/Belgrade")
          .startOf("day")
          .subtract(7, "days")
          .format("x");
        dayEnd = moment().tz("Europe/Belgrade").endOf("day").format("x");
        type = "days";
        break;
      case selectedRange?.value === "last12Months":
        dayBegin = moment()
          .tz("Europe/Belgrade")
          .startOf("day")
          .subtract(1, "year")
          .format("x");
        dayEnd = moment().tz("Europe/Belgrade").endOf("months").format("x");
        type = "months";
        break;
      case selectedRange?.value === "last6Months":
        dayBegin = moment()
          .tz("Europe/Belgrade")
          .startOf("day")
          .subtract(6, "months")
          .format("x");
        dayEnd = moment().tz("Europe/Belgrade").endOf("months").format("x");
        type = "months";
        break;
      case selectedRange?.value === "last30Days":
        dayBegin = moment()
          .tz("Europe/Belgrade")
          .startOf("day")
          .subtract(1, "month")
          .format("x");
        dayEnd = moment().tz("Europe/Belgrade").endOf("day").format("x");
        type = "days";
        break;
      case selectedRange?.value === "currentMonthDays":
        dayBegin = moment().tz("Europe/Belgrade").startOf("month").format("x");
        dayEnd = moment().tz("Europe/Belgrade").endOf("day").format("x");
        type = "days";
        break;
      case selectedRange?.value === "customDateRange":
        if (!customDateFrom || !customDateTo) return;
        dayBegin = moment(new Date(customDateFrom))
          .tz("Europe/Belgrade")
          .startOf("day")
          .format("x");
        dayEnd = moment(new Date(customDateTo))
          .tz("Europe/Belgrade")
          .endOf("day")
          .format("x");
        // Use the selected report type
        type = customReportType;
        break;
      default:
        dayBegin = moment().tz("Europe/Belgrade").startOf("month").format("x");
        dayEnd = moment().tz("Europe/Belgrade").endOf("day").format("x");
        type = "days";
        break;
    }
    try {
      await userReportService.exportAllBusinessUnitsReport(
        dayBegin,
        dayEnd,
        type
      );
    } catch (err) { }
  };

  const renderReportOptions = () => {
    return (
      <div className={styles.optionsTooltip}>
        {reportOptions.map((option, index) => (
          <div
            className={classNames(
              styles.tooltipOption,
              index === 0 && styles.tooltipOptionTop,
              index === reportOptions.length - 1 && styles.tooltipOptionBottom
            )}
            onClick={(e) => {
              setSelectedRange(option);
              e.stopPropagation();
              setRangePickerVisible(false);
              
              // Show date picker popup if custom date range option is selected
              if (option.value === "customDateRange") {
                setDatePickerPopupVisible(true);
              }
            }}
          >
            <p className={styles.reportOptionText}>{option.label}</p>
          </div>
        ))}
      </div>
    );
  };


  const renderDatePickerPopup = () => {
    // Min and max date constraints
    const minDate = moment().subtract(1, 'year').format('YYYY-MM-DD');
    const maxDate = moment().endOf('day').format('YYYY-MM-DD'); // Current date included
    
    // Set default values if not already set
    const defaultFrom = customDateFrom || moment().subtract(7, 'days').format('YYYY-MM-DD');
    const defaultTo = customDateTo || moment().endOf('day').format('YYYY-MM-DD');
    
    // Check if the date range is valid (from <= to)
    const isValidRange = !customDateFrom || !customDateTo || 
                         moment(customDateFrom).isSameOrBefore(moment(customDateTo));

    return (
      <div 
        className={styles.datePickerPopupOverlay}
        onClick={() => setDatePickerPopupVisible(false)}
      >
        <div 
          className={styles.datePickerPopup}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.datePickerPopupHeader}>
            <p className={styles.datePickerPopupTitle}>Izaberite period</p>
            <div 
              className={styles.closeIconWrap}
              onClick={() => setDatePickerPopupVisible(false)}
            >
              ✕
            </div>
          </div>
          
          <div className={styles.datePickerPopupContent}>
            <div className={styles.datePickerContainer}>
              <div className={styles.dateInputWrap}>
                <p>Od</p>
                <input
                  type="date"
                  className={styles.dateInput}
                  value={customDateFrom || defaultFrom}
                  min={minDate}
                  max={maxDate}
                  onChange={(e) => setCustomDateFrom(e.target.value)}
                />
              </div>
              <div className={styles.dateInputWrap}>
                <p>Do</p>
                <input
                  type="date"
                  className={styles.dateInput}
                  value={customDateTo || defaultTo}
                  min={customDateFrom || minDate}
                  max={maxDate}
                  onChange={(e) => setCustomDateTo(e.target.value)}
                />
              </div>
            </div>
            
            <div className={styles.reportTypeSelector}>
              <p className={styles.reportTypeLabel}>Tip izveštaja:</p>
              <div className={styles.reportTypeOptions}>
                <div className={styles.reportTypeOption}>
                  <input 
                    type="radio" 
                    id="typeDays" 
                    name="reportType" 
                    value="days"
                    checked={customReportType === "days"}
                    onChange={() => setCustomReportType("days")}
                  />
                  <label htmlFor="typeDays">Dnevni</label>
                </div>
                <div className={styles.reportTypeOption}>
                  <input 
                    type="radio" 
                    id="typeMonths" 
                    name="reportType" 
                    value="months"
                    checked={customReportType === "months"}
                    onChange={() => setCustomReportType("months")}
                  />
                  <label htmlFor="typeMonths">Mesečni</label>
                </div>
              </div>
            </div>
            
            {!isValidRange && (
              <div className={styles.dateRangeError}>
                <p>Datum "Od" mora biti pre datuma "Do"</p>
              </div>
            )}
            
            <div className={styles.datePickerPopupActions}>
              <div 
                className={classNames(
                  styles.applyButton,
                  (!isValidRange || !customDateFrom || !customDateTo) && styles.disabledButton
                )}
                onClick={() => {
                  // Only close the popup if dates are valid
                  if (isValidRange && customDateFrom && customDateTo) {
                    fetchCustomDateRangeReport();
                    setDatePickerPopupVisible(false);
                  }
                }}
              >
                <p>Primeni</p>
              </div>
              
              <div 
                className={styles.cancelButton}
                onClick={() => setDatePickerPopupVisible(false)}
              >
                <p>Odustani</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderBusinessUnits = () => {
    return allBusinessUnitsCurrentReport.data?.report?.map(
      (businessUnitReport) => {
        return (
          <BusinessUnitReportCard
            businessUnitName={
              businessUnitReport?.companyBusinessUnit?.address.name
            }
            total={Math.round(businessUnitReport.timeValues[0]?.totalAmount, 2)}
            taxTotal={Math.round(
              businessUnitReport.timeValues[0]?.totalTaxAmount,
              2
            )}
            onClick={() => {
              setSelectedBusinessUnit(businessUnitReport?.companyBusinessUnit);
            }}
            address={businessUnitReport?.companyBusinessUnit?.name}
          />
        );
      }
    );
  };

  useEffect(() => {
    if (allBusinessUnitsCurrentReport?.data?.report) {
      if (allBusinessUnitsCurrentReport.data.report[0]) {
        setSelectedBusinessUnits([
          ...selectedBusinessUnits,
          allBusinessUnitsCurrentReport.data.report[0].companyBusinessUnit
            .address.name,
        ]);
      }
    }
  }, [allBusinessUnitsCurrentReport]);

  const businessUnitsHeder = () => {
    return allBusinessUnitsCurrentReport?.data?.report?.map(
      (businessUnitReport, index) => {
        const buSelected = selectedBusinessUnits.find(
          (bu) => bu === businessUnitReport.companyBusinessUnit.address.name
        )
          ? true
          : false;
        return (
          <div
            className={styles.businessUnitWrap}
            onClick={() => {
              const found = selectedBusinessUnits.find(
                (bu) =>
                  bu === businessUnitReport.companyBusinessUnit.address.name
              );
              if (found) {
                const newSelectedBusinessUnits = selectedBusinessUnits.filter(
                  (bu) => bu !== found
                );
                setSelectedBusinessUnits(newSelectedBusinessUnits);
              } else {
                setSelectedBusinessUnits([
                  ...selectedBusinessUnits,
                  businessUnitReport.companyBusinessUnit.address.name,
                ]);
              }
            }}
          >
            <div
              className={classNames(
                !buSelected ? styles.cirleUnactive : styles.circle
              )}
              style={{ backgroundColor: buSelected && colors[index % 5] }}
            ></div>
            <p
              style={{ color: !buSelected && "#ecebeb", whiteSpace: "nowrap" }}
            >
              {businessUnitReport.companyBusinessUnit.address.name.replaceAll(
                '"',
                ""
              )}
            </p>
          </div>
        );
      }
    );
  };

  const graphViewOptions = () => {
    return viewOptions.map((option) => (
      <div
        className={classNames(
          styles.graphViewWrap,
          option === selectedViewOption && styles.graphViewWrapSelected
        )}
        onClick={() => {
          setSelectedViewOption(option);
        }}
      >
        <p
          className={classNames(
            styles.viewOption,
            option === selectedViewOption && styles.viewOptionSelected
          )}
        >
          {option}
        </p>
      </div>
    ));
  };

  const graphCardHeader = (renderHeader = true) => {
    return (
      <>
        <div className={styles.graphTitleWrap}>
          <div className={styles.leftWrap}>
            {selectedRange?.value !== "last24Hours" &&
              <div
                className={styles.exportBtnWrap}
                onClick={onExport}
              ><div
                className={styles.exportBt}
              >
                  <p>{isMobile ? "Izvezi " : "Izvezi izveštaj "}</p>
                  <img src={XlsIcon} alt="xls" className={styles.xlsIcon} />
                </div>
              </div>}
          </div>
          <div className={styles.rightWrap}>
            <div className={styles.graphViewOptionsWrap}>
              {graphViewOptions()}
            </div>
            {<div
              className={styles.rangePicker}
              onClick={(e) => {
                setRangePickerVisible(true);
                e.stopPropagation();
              }}
            >
              <div className={styles.wrap1}>
                <p className={styles.selectedReportOption}>
                  {isMobile ? selectedRange.labelSm : selectedRange.label}
                </p>
                {/* <img src={ChevronDown} alt="xls" className={styles.chevron} /> */}

              </div>
              {rangePickerVisible && renderReportOptions()}
            </div>}
            
            {selectedRange?.value === "customDateRange" && customDateFrom && customDateTo && (
              <div 
                className={styles.customDateDisplay}
                onClick={(e) => {
                  e.stopPropagation();
                  setDatePickerPopupVisible(true);
                }}
              >
                <p>
                  {isMobile ? 
                    `${moment(new Date(customDateFrom)).format("DD.MM")} - ${moment(new Date(customDateTo)).format("DD.MM")}` :
                    `${moment(new Date(customDateFrom)).format("DD.MM.YYYY")} - ${moment(new Date(customDateTo)).format("DD.MM.YYYY")}`
                  }
                  {isMobile ? 
                    (customReportType === "months" ? " (M)" : " (D)") : 
                    (customReportType === "months" ? " (Mesečno)" : " (Dnevno)")
                  }
                </p>
              </div>
            )}
          </div>
        </div>
        {renderHeader && (
          <div className={styles.graphHeader}>{businessUnitsHeder()}</div>
        )}
      </>
    );
  };

  const renderGraphCard = () => {
    let data = null;
    let attempt = null;
    
    // For custom date range, determine whether to use daily or monthly graph
    if (selectedRange.value === "customDateRange") {
      attempt = customDateReport.attempt;
      data = customDateReport.data;
      
      // Show monthly or daily graph based on the selected type
      if (customReportType === "months") {
        return (
          <div
            className={classNames(
              isMobile && selectedViewOption !== "Graficki"
                ? styles.graphCardMobile
                : styles.graphCard
            )}
          >
            <>
              {selectedViewOption === "Graficki" && graphCardHeader()}
              {attempt && <div className={styles.graphCardCenter}><ThreeDots fill="#cbc9c9" /></div>}
              {!attempt && selectedViewOption === "Graficki" && (
                <div className={styles.graphWrap}>
                  <MonthGraph
                    data={data}
                    selectedBusinessUnits={selectedBusinessUnits}
                    variant={"lines"}
                  />
                </div>
              )}
            </>

            {!attempt && selectedViewOption === "Tabelarno" && !isMobile && (
              <>
                {graphCardHeader(false)}
                <TableGraph data={data} variant={"lines"} onExport={onExport} />
              </>
            )}
            {!attempt && selectedViewOption === "Tabelarno" && isMobile && (
              <>
                {graphCardHeader(false)}
                <MobileTableGraph data={data} onExport={onExport} />
              </>
            )}
          </div>
        );
      }
    }
    
    switch (true) {
      case selectedRange.value === "last7Days" ||
        selectedRange.value === "last30Days" ||
        selectedRange.value === "currentMonthDays" ||
        selectedRange.value === "last24Hours" ||
        (selectedRange.value === "customDateRange" && customReportType === "days"):
        if (selectedRange.value === "last7Days") {
          attempt = allBusinessUnitsLast7DaysReport.attempt;
          data = allBusinessUnitsLast7DaysReport.data;
        } else if (selectedRange.value === "last30Days") {
          attempt = allBusinessUnitsLast30DaysReport.attempt;
          data = allBusinessUnitsLast30DaysReport.data;
        } else if (selectedRange.value === "currentMonthDays") {
          attempt = allBusinessUnitsCurrentMonthDaysReport.attempt;
          data = allBusinessUnitsCurrentMonthDaysReport.data;
        } else if (selectedRange.value === "last24Hours") {
          attempt = allBusinessUnits24hReport.attempt;
          data = allBusinessUnits24hReport.data;
        } else if (selectedRange.value === "customDateRange") {
          attempt = customDateReport.attempt;
          data = customDateReport.data;
        }
        return (
          <div
            className={classNames(
              isMobile && selectedViewOption !== "Graficki"
                ? styles.graphCardMobile
                : styles.graphCard
            )}
          >
            <>
              {selectedViewOption === "Graficki" && graphCardHeader()}
              {attempt && <div className={styles.graphCardCenter}><ThreeDots fill="#cbc9c9" /></div>}
              {!attempt && selectedViewOption === "Graficki" && (
                <div className={styles.graphWrap}>
                  <DaysGraph
                    data={data}
                    selectedBusinessUnits={selectedBusinessUnits}
                    variant={
                      selectedRange.value === "last24Hours" ? "bar" : "line"
                    }
                    type={
                      selectedRange.value === "last24Hours" ? "hours" : "days"
                    }
                  />
                </div>
              )}
            </>

            {!attempt && selectedViewOption === "Tabelarno" && !isMobile && (
              <>
                {graphCardHeader(false)}
                <TableGraph data={data} variant={"lines"} />
              </>
            )}
            {!attempt && selectedViewOption === "Tabelarno" && isMobile && (
              <>
                {graphCardHeader(false)}
                <MobileTableGraph data={data} onExport={onExport} />
              </>
            )}
          </div>
        );

      case selectedRange.value === "last12Months" ||
        selectedRange.value === "last6Months":
        attempt = false;
        data = [];
        if (selectedRange.value === "last12Months") {
          attempt = allBusinessUnitsYearReports.attempt;
          data = allBusinessUnitsYearReports.data;
        } else if (selectedRange.value === "last6Months") {
          attempt = allBusinessUnitsLast6MonthsReport.attempt;
          data = allBusinessUnitsLast6MonthsReport.data;
        }
        return (
          <div
            className={classNames(
              isMobile && selectedViewOption !== "Graficki"
                ? styles.graphCardMobile
                : styles.graphCard
            )}
          >
            <>
              {selectedViewOption === "Graficki" && graphCardHeader()}
              {attempt && <div className={styles.graphCardCenter}><ThreeDots fill="#cbc9c9" /></div>}
              {!attempt && selectedViewOption === "Graficki" && (
                <div className={styles.graphWrap}>
                  <MonthGraph
                    data={data}
                    selectedBusinessUnits={selectedBusinessUnits}
                    variant={"lines"}
                  />
                </div>
              )}
            </>

            {!attempt && selectedViewOption === "Tabelarno" && !isMobile && (
              <>
                {graphCardHeader(false)}
                <TableGraph data={data} variant={"lines"} onExport={onExport} />
              </>
            )}
            {!attempt && selectedViewOption === "Tabelarno" && isMobile && (
              <>
                {graphCardHeader(false)}
                <MobileTableGraph data={data} onExport={onExport} />
              </>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <DashboardLayout>
      <BusinessUnitReportModal
        modalOpen={selectedBusinessUnit !== null}
        onClose={() => setSelectedBusinessUnit(null)}
        businessUnit={selectedBusinessUnit}
      />
      <div
        className={styles.content}
        onClick={() => {
          setRangePickerVisible(false);
        }}
      >
        <div className={styles.topPart}>
          <p className={styles.title}>Poslovni prostori</p>
          <BannerContainer />
        </div>
        <div className={styles.dashboardContent}>
          <div className={styles.dashboardList}>{renderBusinessUnits()}</div>
          {renderGraphCard(false)}
        </div>
      </div>
      {datePickerPopupVisible && renderDatePickerPopup()}
    </DashboardLayout>
  );
};

export default Dashboard;
